import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import KnowledgeLayout from './shared/components/KnowledgeLayout';
import ErrorBoundary from './shared/components/ErrorBoundary';
import LoadingSpinner from './shared/components/LoadingSpinner';
import { PermissionRouteGuard } from './shared/components/PermissionRouteGuard';
import { Permission, UserRole } from '@amzn/neura-knowledge-management-model';
import { TaxonomyProvider } from './shared/context/TaxonomyContext';

// Lazy-loaded pages — code splitting per route (Phase 13.3.1)
const BrowseArticlesPage = React.lazy(() => import('./features/articles/BrowseArticlesPage'));
const ArticleViewPage = React.lazy(() => import('./features/articles/ArticleViewPage'));
const ArticleEditorPage = React.lazy(() => import('./features/articles/ArticleEditorPage'));
const ValidationDashboardPage = React.lazy(
  () => import('./features/validation/ValidationDashboardPage')
);
const KnowledgeHomePage = React.lazy(() => import('./features/dashboard/KnowledgeHomePage'));
const LaunchMilestonesPage = React.lazy(() => import('./features/milestones/LaunchMilestonesPage'));
const KnowledgeGapsPage = React.lazy(() => import('./features/gaps/KnowledgeGapsPage'));
const SlackChannelConfigPage = React.lazy(() => import('./features/admin/slack/SlackChannelConfigPage'));
const RecognitionDashboardPage = React.lazy(() => import('./features/recognition/RecognitionDashboardPage'));
const LifecycleAnalyticsPage = React.lazy(() => import('./features/lifecycle/LifecycleAnalyticsPage'));
const OperationsMonitoringPage = React.lazy(() => import('./features/operations/OperationsMonitoringPage'));
const QualityDashboardPage = React.lazy(() => import('./features/quality/QualityDashboardPage'));
const KnowledgeCoveragePage = React.lazy(() => import('./features/coverage/KnowledgeCoveragePage'));
const ArticleViewerPage = React.lazy(() => import('./features/viewer/ArticleViewerPage'));
const ViewerBookmarksPage = React.lazy(() => import('./features/viewer/ViewerBookmarksPage'));
const TaxonomyBrowserPage = React.lazy(() => import('./features/taxonomy/TaxonomyBrowserPage'));
const BarRaiserAdminPage = React.lazy(() => import('./features/taxonomy/BarRaiserAdminPage'));
const TaxonomyAdminPage = React.lazy(() => import('./features/taxonomy/TaxonomyAdminPage'));
const TemplateAdminPage = React.lazy(() => import('./features/admin/TemplateAdminPage'));

export default function App() {
  return (
    <ErrorBoundary>
      <TaxonomyProvider>
      <React.Suspense fallback={<LoadingSpinner size="large" label="Loading page..." />}>
        <Routes>
          <Route path="/" element={<Navigate to="/knowledge/browse" replace />} />

          {/* Knowledge portal routes — wrapped in shared layout */}
          <Route
            path="/knowledge/*"
            element={
              <KnowledgeLayout>
                <Routes>
                  <Route path="browse" element={<BrowseArticlesPage />} />
                  <Route path="articles/:articleId" element={<ArticleViewPage />} />
                  <Route path="create" element={<PermissionRouteGuard requiredPermission={Permission.ARTICLE_CREATE}><ArticleEditorPage /></PermissionRouteGuard>} />
                  <Route path="articles/:articleId/edit" element={<PermissionRouteGuard requiredPermission={Permission.ARTICLE_CREATE}><ArticleEditorPage /></PermissionRouteGuard>} />
                  <Route path="validation" element={<PermissionRouteGuard requiredPermission={Permission.ARTICLE_VALIDATE}><ValidationDashboardPage /></PermissionRouteGuard>} />
                  <Route path="dashboard" element={<KnowledgeHomePage />} />
                  <Route path="milestones" element={<LaunchMilestonesPage />} />
                  <Route path="gaps" element={<PermissionRouteGuard requiredRole={UserRole.CHAMPION}><KnowledgeGapsPage /></PermissionRouteGuard>} />
                  <Route path="recognition" element={<RecognitionDashboardPage />} />
                  <Route path="lifecycle" element={<PermissionRouteGuard requiredRole={UserRole.BAR_RAISER}><LifecycleAnalyticsPage /></PermissionRouteGuard>} />
                  <Route path="quality" element={<PermissionRouteGuard requiredRole={UserRole.BAR_RAISER}><QualityDashboardPage /></PermissionRouteGuard>} />
                  <Route path="taxonomy" element={<TaxonomyBrowserPage />} />
                  <Route path="*" element={<Navigate to="dashboard" replace />} />
                </Routes>
              </KnowledgeLayout>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <KnowledgeLayout>
                <Routes>
                  <Route path="slack-channels" element={<PermissionRouteGuard requiredRole={UserRole.ADMIN}><SlackChannelConfigPage /></PermissionRouteGuard>} />
                  <Route path="operations" element={<PermissionRouteGuard requiredRole={UserRole.ADMIN}><OperationsMonitoringPage /></PermissionRouteGuard>} />
                  <Route path="coverage" element={<PermissionRouteGuard requiredRole={UserRole.ADMIN}><KnowledgeCoveragePage /></PermissionRouteGuard>} />
                  <Route path="bar-raiser" element={<PermissionRouteGuard requiredRole={UserRole.BAR_RAISER}><BarRaiserAdminPage /></PermissionRouteGuard>} />
                  <Route path="taxonomy" element={<PermissionRouteGuard requiredRole={UserRole.ADMIN}><TaxonomyAdminPage /></PermissionRouteGuard>} />
                  <Route path="templates" element={<PermissionRouteGuard requiredRole={UserRole.ADMIN}><TemplateAdminPage /></PermissionRouteGuard>} />
                  <Route path="*" element={<Navigate to="operations" replace />} />
                </Routes>
              </KnowledgeLayout>
            }
          />

          {/* Public article viewer — no sidebar (Phase 11) */}
          <Route path="/articles/:articleId" element={<ArticleViewerPage />} />
          <Route path="/bookmarks" element={<ViewerBookmarksPage />} />
        </Routes>
      </React.Suspense>
      </TaxonomyProvider>
    </ErrorBoundary>
  );
}
